import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppService } from '../app.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class RBACService {

  // These features and actions must remain in sync with app/Constants/RBACPermissions.php in the API at all times.

  public static readonly RBAC_A = {
    A_CREATE: 'create',
    A_VIEW: 'view',
    A_EDIT: 'edit',
    A_DELETE: 'delete'
  };

  public static readonly RBAC_F = {
    CORE_PERMISSION_USER_PROFILE: 'core-user-profile',
    F_ACCOUNT_SETTINGS: 'account-settings',
    F_ACCOUNT_COMPANY_DOCUMENTS: 'account-company-documents',
    F_ACCOUNT_RBAC_ROLES: 'account-rbac-roles',
    F_ACCOUNT_NOTIFICATION_ROLES: 'account-notification-roles',
    F_ACCOUNT_TAGS: 'account-tags',
    F_ACCOUNT_INDUCTIONS: 'account-inductions',
    F_SITES: 'sites', // create, view, edit, delete.
    F_SITES_MANAGERS: 'sites-managers',
    F_SITES_SAFETY_PLANS: 'sites-safety-plans', // create, edit, delete
    F_SITES_DOCUMENTS: 'sites-documents',
    F_SITES_HAZARDS_RISKS: 'sites-hazards-risks',
    F_SITES_HAZARDOUS_SUBSTANCES: 'sites-hazardous-substances',
    F_SITES_SAFETY_OBSERVATIONS: 'sites-safety-observations',
    F_SITES_INCIDENTS: 'sites-incidents',
    F_SITES_TOOLBOX_TALKS: 'sites-toolbox-talks',
    F_SITES_INSPECTIONS_AUDITS: 'sites-inspections-audits',
    F_SITES_TASK_ANALYSIS_DOCUMENTS: 'sites-task-analysis-documents',
    F_SITES_TA_JSA_SWMS: 'sites-tajsaswms',
    F_SITES_QR_CODES: 'sites-qr-codes',
    F_SITES_EEP: 'sites-emergency-evacuation-plan', // view, edit, delete.
    F_SITES_USERS_WORKED_ON: 'sites-users-worked-on',
    F_SITES_SPECIFIED_SYSTEMS: 'sites-specified-systems',
    F_SITES_ASSETS: 'sites-assets',
    F_CONTRACTORS: 'contractors',
    F_CONTRACTORS_SAFETY_PLANS: 'contractors-safety-plans', // create, edit, delete
    F_CONTRACTORS_DOCUMENTS: 'contractors-documents',
    F_CONTRACTORS_PRE_QUALIFICATIONS: 'contractors-pre-qualifications',
    F_CONTRACTORS_SUBCONTRACTORS: 'contractors-subcontractors',
    F_CONTRACTORS_EMPLOYEES: 'contractors-employees',
    F_EMPLOYEES: 'employees',
    F_EMPLOYEES_ACCESS_REQUESTS: 'employees-access-requests',
    F_EMPLOYEES_TRAINING_COMPETENCIES: 'employees-training-competencies',
    F_EMPLOYEES_SITE_ASSOCIATIONS: 'employees-site-associations',
    F_SAFETY_PLANS: 'safety-plans', // view, edit, delete.
    F_TASKS: 'tasks',
    F_FORMS: 'forms',
    F_REGISTERS_INCIDENTS: 'registers-incidents',
    F_REGISTERS_SAFETY_OBSERVATIONS: 'registers-safety-observations',
    F_REGISTERS_HAZARDS_RISKS: 'registers-hazards-risks',
    F_REGISTERS_HAZARDOUS_SUBSTANCES: 'registers-hazardous-substances',
    F_REGISTERS_INSPECTIONS_AUDITS: 'registers-inspections-audits',
    F_REGISTERS_TOOLBOX_TALKS: 'registers-toolbox-talks',
    F_REGISTERS_TOOLS: 'registers-tools',
    F_REGISTERS_ASSETS: 'registers-assets',
    F_REGISTERS_ASSET_TYPES: 'registers-asset-types',
    F_REGISTERS_VENDORS: 'registers-vendors',
    F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS: 'templates-task-analysis-documents',
    F_TEMPLATES_TA_JSA_SWMS: 'templates-tajsaswms',
    F_TEMPLATES_DOCUMENTS: 'templates-documents',
    F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS: 'templates-contractor-pre-qualifications',
    F_TEMPLATES_INSPECTIONS_AUDITS: 'templates-inspections-audits',
    F_TEMPLATES_TOOLBOX_TALKS: 'templates-toolbox-talks',
    F_REPORTS_SITE_ACCESS: 'reports-site-access',
    F_REPORTS_VISITORS: 'reports-visitors',
    F_MESSAGES: 'messages',
    F_CLIENT_SITES: 'client-sites',
    F_CLIENT_PRE_QUALIFICATIONS: 'client-pre-qualifications',
    F_NETWORKED_USERS: 'networked-users',
    F_NETWORKED_ACCOUNTS: 'networked-accounts'
  };

  // This is used to keep track of the user's permissions.
  permissions: string[] = [];

  // Incremented every time the permission list changes. Components that resolve permissions into
  // boolean flags subscribe to permissionsChanged$ so they can recompute those flags.
  private permissionsVersionSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /**
   * Emits whenever the user's permissions change (sign in, sign out, account switch, super admin
   * quick access). Components that cache permission results in boolean flags MUST recompute them on
   * emission, otherwise the flags go stale.
   *
   * Routed components generally do not need this: switching accounts re-navigates and therefore
   * recreates them, so resolving flags once in ngOnInit is enough. It IS required for persistent
   * components that are never destroyed, such as the app shell and the top navigation.
   */
  readonly permissionsChanged$: Observable<number> = this.permissionsVersionSubject.asObservable();

  constructor(
    private app: AppService,
    private utilsService: UtilsService
  ) { }

  /**
   * Applies the provided permissions to the user's permissions list.
   *
   * Callers MUST apply the user, account and user account link data BEFORE calling this, because
   * userCanAccess() also reads app.user.is_sitesoft_admin. Applying permissions last means the
   * change notification is emitted only once the auth state is fully consistent.
   *
   * @param permissions
   */
  applyPermissions(permissions: string[]): void {
    // Mutated in place rather than reassigned, so the array identity stays stable. clearPermissions()
    // also mutates in place, and mixing the two styles is the trap: anything that captured the
    // reference (or bound it in a template) would observe a clear but never see new permissions.
    // Nothing captures it today, and keeping both methods in-place stops that becoming a landmine.
    this.permissions.length = 0;
    this.permissions.push(...permissions);
    this.notifyPermissionsChanged();
  }

  /**
   * Clears all permissions from the user's permissions list.
   */
  clearPermissions(): void {
    this.permissions.length = 0;
    this.notifyPermissionsChanged();
  }

  /**
   * Notifies subscribers that the permission list changed so cached boolean flags can be recomputed.
   */
  private notifyPermissionsChanged(): void {
    this.permissionsVersionSubject.next(this.permissionsVersionSubject.value + 1);
  }

  /**
   * Determines if the user has access based on their permissions.
   *
   * @param {string[]} permissions - The list of required permissions to check against the user's permissions.
   * @param exact - If true, the user must have all the required permissions. If false, the user must have at least one of the required permissions. Defaults to false.
   * @return {boolean} Returns true if the user has at least one of the required permissions, otherwise false.
   */
  userCanAccess(permissions: string[], exact: boolean = false): boolean {
    // Super admins bypass all permissions.
    //
    // Optional chaining is a fail-safe: app.user is eagerly initialised to a UserModel, but a
    // malformed auth or token-refresh response can overwrite it with undefined. Failing safe here
    // means skipping the bypass and still evaluating the permission list, so a missing user can
    // never grant access, only withhold it.
    if ( this.app.user?.is_sitesoft_admin ) {
      return true;
    }

    if ( exact ) {
      return permissions.every((permission: string): boolean => this.permissions.includes(permission));
    }
    return permissions.some((permission: string): boolean => this.permissions.includes(permission));
  }

  /**
   * Whether the user holds no feature permission at all, meaning nothing beyond the always-available
   * core user profile permission.
   *
   * Such a user is a field worker rather than a portal administrator, so the web portal has nothing
   * to offer them. This replaced checks on `user_account_link.permissions == 'Employee'`, the legacy
   * role label that RBAC retired.
   *
   * Super admins are never field workers. They bypass RBAC and therefore hold no account-scoped
   * abilities of their own, so counting permissions would wrongly classify them. They are excluded
   * explicitly instead.
   *
   * @return {boolean} True when the user has only the core user profile permission, or none at all.
   */
  holdsNoFeaturePermissions(): boolean {
    if ( this.app.user?.is_sitesoft_admin ) {
      return false;
    }
    return !this.permissions.some(
      (permission: string): boolean => permission !== RBACService.RBAC_F.CORE_PERMISSION_USER_PROFILE
    );
  }

  /**
   * Displays generic access denied message to the user.
   *
   * This method shows a toast notification indicating that the user does not have the necessary permissions to perform the requested action.
   *
   * @return {void} No return value.
   */
  showGenericAccessDeniedMessage(custom_message?: string): void {
    this.utilsService.showToast(custom_message ?? `You do not have permission to perform this action.`);
  }

  /**
   * Whether the partner access lock applies to the current user for a partner-managed target.
   * Super admins are never locked (the API applies the same bypass, see isSystemsAdministrator()).
   *
   * This is not a permission check, so it sits beside userCanAccess() without replacing it.
   *
   * @param isPartnerManaged Whether the target (role, user or membership) is partner-managed.
   * @return {boolean} True when the target must be shown read-only to the current user.
   */
  partnerLockApplies(isPartnerManaged: boolean): boolean {
    return !!isPartnerManaged && !this.app.user?.is_sitesoft_admin;
  }

  /**
   * The message shown when a client tries to change partner-managed access. Mirrors the API (R4.4).
   *
   * @return {string} The managed-by message with the brand name.
   */
  partnerManagedMessage(): string {
    const appName: string = this.utilsService.getAppName();
    return `This access is managed by ${appName} and can only be changed by ${appName} support.`;
  }

  /**
   * A sentence naming the partner users that a multi-user role request left unchanged, built from the
   * API's `skipped` rows ({ user_id, contact_person, reason, message }, api-spec 7.2a). Empty when none.
   *
   * @param skipped The `skipped` rows from the API response, if any.
   * @return {string} For example " Alex Partner was left unchanged because their access is managed by SiteConnect."
   */
  partnerSkippedNotice(skipped: any[] | null | undefined): string {
    if (!Array.isArray(skipped) || skipped.length === 0) {
      return '';
    }
    const names: string = skipped
      .map((row: any): string => row?.contact_person || `User ${row?.user_id}`)
      .join(', ');
    const verb: string = skipped.length === 1 ? 'was' : 'were';
    return ` ${names} ${verb} left unchanged because their access is managed by ${this.utilsService.getAppName()}.`;
  }

  /**
   * Super admin linking (api-spec 6.7): a warning sentence when any `partner_sync` row of a
   * `POST v2/admin/users-accounts` response failed (for example the partner sync lock was busy), so the
   * partner role was not applied in that account. Empty when nothing failed.
   *
   * @param response The `POST v2/admin/users-accounts` response body.
   * @return {string} The warning, starting with a space, or ''.
   */
  partnerSyncFailureNotice(response: any): string {
    const rows: any[] = Array.isArray(response?.partner_sync) ? response.partner_sync : [];
    const failed: any[] = rows.filter((row: any): boolean => row?.status === 'failed');
    if (failed.length === 0) {
      return '';
    }
    const names: string = failed
      .map((row: any): string => row?.account_name || `Account ${row?.account_id}`)
      .join(', ');
    return ` Warning: the partner role could not be applied in ${names}. Use Resync on the partner's Partner tab (Admin > Users > edit) to apply it.`;
  }

  /**
   * Generates a permission string based on the provided feature and action.
   * Combines the feature and action into a single string in the format `feature:action`.
   * Throws an error if the provided feature or action is invalid.
   *
   * @param {string} feature - The feature to check permissions for. Must be a valid entry in the predefined list of system features.
   * @param {string} action - The action to perform on the feature. Must be a valid entry in the predefined list of system actions.
   * @return {string} The generated permission string in lowercase format.
   */
  static getPermission(feature: string, action: string): string {
    if (!Object.values(this.RBAC_F).includes(feature)) {
      throw new Error(`Invalid feature provided. Feature must be one of: ${Object.values(this.RBAC_F).join(', ')}.`);
    }
    if (!Object.values(this.RBAC_A).includes(action)) {
      throw new Error(`Invalid action provided. Action must be one of: ${Object.values(this.RBAC_A).join(', ')}.`);
    }
    return (feature + ':' + action).toLowerCase();
  }
}
